/**
 * EchelonnementPreview — Bloc visuel partagé "Paiement échelonné disponible".
 *
 * Affiche le tableau récapitulatif + l'échéancier prévisionnel généré par
 * /api/echelonnement/preview. Cohérent avec le bloc inséré dans le PDF
 * (Proposition ESS / Devis ESS) pour que le membre voie la même chose à
 * l'écran et sur son PDF.
 *
 * Super Admin uniquement : peut personnaliser acompte_pct, nb_echeances et
 * offsets_days via la modale "Personnaliser l'échéancier".
 *
 * V36.5 — 28/05/2026.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Settings, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getAuthHeader } from '../services/tokenService';
import { DateFrInput } from './DateFrInput';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtEur = (v) => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function EchelonnementPreview({ montantTotalEur, onPlanChange, context = 'default' }) {
  const { user } = useAuth() || {};
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({
    acompte_pct: 60,
    nb_echeances: 3,
    offsets_days: '30,60,90',
    acompte_date: '',
    dates_iso: [],
    montants_eur: [],
    force_eligible: false,
  });

  const fetchPlan = useCallback(async (overrides = null) => {
    if (!montantTotalEur || montantTotalEur <= 0) return;
    setLoading(true);
    try {
      const payload = { montant_total_eur: montantTotalEur, context };
      // V36.73 — Si Super Admin a personnalisé, on transmet le détail au
      // parent pour pouvoir l'injecter dans le PDF (echelonnement_override).
      let customPayloadForPdf = null;
      if (overrides && isSuperAdmin) {
        payload.acompte_pct = Number(overrides.acompte_pct) / 100;
        payload.nb_echeances = Number(overrides.nb_echeances);
        const offs = String(overrides.offsets_days || '')
          .split(',').map((s) => parseInt(s.trim(), 10)).filter(Number.isFinite);
        if (offs.length === Number(overrides.nb_echeances)) payload.offsets_days = offs;
        const dates = Array.from({ length: Number(overrides.nb_echeances) }, (_, i) => (overrides.dates_iso || [])[i] || null);
        if (dates.some(Boolean)) payload.dates_iso = dates;
        if (overrides.acompte_date) payload.acompte_date = overrides.acompte_date;
        const montants = Array.from({ length: Number(overrides.nb_echeances) }, (_, i) => {
          const v = String((overrides.montants_eur || [])[i] ?? '').replace(',', '.').trim();
          return v === '' ? null : Number(v);
        });
        if (montants.some((v) => v !== null)) payload.montants_eur = montants;
        payload.force_eligible = !!overrides.force_eligible;
        customPayloadForPdf = {
          is_custom: true,
          acompte_pct: payload.acompte_pct,
          nb_echeances: payload.nb_echeances,
          offsets_days: payload.offsets_days || null,
          acompte_date: payload.acompte_date || null,
          dates_iso: payload.dates_iso || null,
          montants_eur: payload.montants_eur || null,
          force_eligible: payload.force_eligible,
          seuil_eur: null,
        };
      }
      const res = await axios.post(`${API}/echelonnement/preview`, payload, { headers: getAuthHeader() });
      const p = res.data?.plan;
      setPlan(p);
      if (customPayloadForPdf) customPayloadForPdf.acompte_pct = p.acompte_pct;
      // Marqueur d'override custom (utilisé par le parent CalculateurBATICO
      // pour transmettre le bon `echelonnement_override` au backend PDF).
      if (onPlanChange) {
        onPlanChange(customPayloadForPdf ? { ...p, ...customPayloadForPdf } : { ...p, is_custom: false });
      }
    } catch (err) {
      // Non-bloquant ; seule une personnalisation refusée est signalée
      if (overrides && err?.response?.status === 400) toast.error(err.response.data?.detail || 'Échéancier refusé');
    } finally {
      setLoading(false);
    }
  }, [montantTotalEur, isSuperAdmin, onPlanChange, context]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  if (!plan && !loading) return null;

  if (loading && !plan) {
    return (
      <div className="my-4 p-4 rounded-xl border-2 border-amber-300 bg-amber-50/40 flex items-center gap-2 text-sm text-amber-800">
        <Loader2 className="h-4 w-4 animate-spin" /> Calcul du paiement échelonné…
      </div>
    );
  }

  // Non éligible : message court
  if (plan && !plan.eligible) {
    return (
      <div className="my-4 p-3 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-600" data-testid="echelonnement-non-eligible">
        ℹ️ {plan.note}
        {plan.montant_manquant_eur > 0 && (
          <span className="ml-1">Il manque <strong>{fmtEur(plan.montant_manquant_eur)} €</strong>.</span>
        )}
        {isSuperAdmin && (
          <Button
            variant="ghost" size="sm"
            onClick={() => { setShowCustom(true); }}
            className="ml-2 h-6 px-2 text-xs"
            data-testid="echelonnement-force-btn"
          >
            <Settings className="h-3 w-3 mr-1" /> Forcer (Super Admin)
          </Button>
        )}
        {showCustom && isSuperAdmin && <CustomPanel custom={custom} setCustom={setCustom} onApply={fetchPlan} onClose={() => setShowCustom(false)} />}
      </div>
    );
  }

  // Éligible : tableau + échéancier
  return (
    <div className="my-4 p-4 rounded-xl border-2 border-amber-400 bg-amber-50/60" data-testid="echelonnement-block">
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wider">
          Option : Paiement échelonné disponible
        </h3>
        {isSuperAdmin && (
          <Button
            variant="ghost" size="sm"
            onClick={() => setShowCustom(true)}
            className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-100"
            data-testid="echelonnement-customize-btn"
          >
            <Settings className="h-3.5 w-3.5 mr-1" /> Personnaliser
          </Button>
        )}
      </div>
      <p className="text-xs text-amber-900/80 mb-3">{plan.note}</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3 text-sm">
        <div className="bg-white rounded p-2 border border-amber-200">
          <div className="text-[10px] text-slate-500 uppercase">Montant total TTC</div>
          <div className="font-bold font-mono text-amber-900">{fmtEur(plan.montant_total_eur)} €</div>
        </div>
        <div className="bg-white rounded p-2 border border-amber-200">
          <div className="text-[10px] text-slate-500 uppercase">Acompte ({(plan.acompte_pct * 100).toFixed(0)} %){plan.acompte_date_fr ? ` — le ${plan.acompte_date_fr}` : ''}</div>
          <div className="font-bold font-mono text-amber-900">{fmtEur(plan.acompte_eur)} €</div>
        </div>
        <div className="bg-white rounded p-2 border border-amber-200">
          <div className="text-[10px] text-slate-500 uppercase">Solde via {plan.nb_echeances} SEPA</div>
          <div className="font-bold font-mono text-amber-900">{fmtEur(plan.sepa_total_eur)} €</div>
        </div>
      </div>

      <div className="bg-white rounded border border-amber-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-amber-100/60">
            <tr>
              <th className="text-left px-3 py-1.5 font-semibold">Échéance</th>
              <th className="text-center px-3 py-1.5 font-semibold">Date</th>
              <th className="text-right px-3 py-1.5 font-semibold">Montant</th>
            </tr>
          </thead>
          <tbody>
            {(plan.echeances || []).map((e) => (
              <tr key={e.index} className="border-t border-amber-100" data-testid={`echeance-${e.index}`}>
                <td className="px-3 py-1.5">{e.label}</td>
                <td className="px-3 py-1.5 text-center">{e.date_fr}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtEur(e.amount_eur)} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-amber-900/70 italic mt-2">
        Mandat SEPA mis en place via Stripe après signature. RIB de prélèvement : Qonto.
      </p>

      {showCustom && isSuperAdmin && <CustomPanel custom={custom} setCustom={setCustom} onApply={fetchPlan} onClose={() => setShowCustom(false)} />}
    </div>
  );
}

function CustomPanel({ custom, setCustom, onApply, onClose }) {
  return (
    <div className="mt-3 p-3 rounded-lg border-2 border-indigo-300 bg-indigo-50/60" data-testid="echelonnement-custom-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-indigo-700 uppercase">Personnaliser (Super Admin)</span>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="h-3 w-3" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <Label className="text-[10px]">Acompte (%)</Label>
          <Input
            type="number" min="0" max="100" value={custom.acompte_pct}
            onChange={(e) => setCustom({ ...custom, acompte_pct: e.target.value })}
            className="h-8 text-sm" data-testid="custom-acompte-input"
          />
        </div>
        <div>
          <Label className="text-[10px]">Nb échéances</Label>
          <Input
            type="number" min="1" max="24" value={custom.nb_echeances}
            onChange={(e) => setCustom({ ...custom, nb_echeances: e.target.value })}
            className="h-8 text-sm" data-testid="custom-nb-input"
          />
        </div>
      </div>
      <div className="mb-2">
        <Label className="text-[10px]">Offsets (jours, séparés par virgules) — utilisés pour les mensualités sans date</Label>
        <Input
          value={custom.offsets_days}
          onChange={(e) => setCustom({ ...custom, offsets_days: e.target.value })}
          placeholder="30,60,90"
          className="h-8 text-sm font-mono" data-testid="custom-offsets-input"
        />
      </div>
      <DatesPanel custom={custom} setCustom={setCustom} />
      <label className="flex items-center gap-2 text-xs text-indigo-700 mb-3">
        <input
          type="checkbox" checked={custom.force_eligible}
          onChange={(e) => setCustom({ ...custom, force_eligible: e.target.checked })}
          data-testid="custom-force-checkbox"
        />
        Forcer même si montant ≤ 2500 €
      </label>
      <Button
        size="sm"
        onClick={() => { onApply(custom); onClose(); }}
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
        data-testid="custom-apply-btn"
      >
        Appliquer
      </Button>
    </div>
  );
}

function DatesPanel({ custom, setCustom }) {
  const nb = Math.max(1, Math.min(Number(custom.nb_echeances) || 0, 24));
  const setListe = (cle, i, v) => {
    const d = Array.from({ length: nb }, (_, k) => (custom[cle] || [])[k] ?? '');
    d[i] = v;
    setCustom({ ...custom, [cle]: d });
  };
  return (
    <div className="mb-3 rounded-md border border-indigo-200 bg-white/70 p-2" data-testid="custom-dates-panel">
      <Label className="text-[10px]">Dates programmées (JJ/MM/AAAA ou calendrier) et montants</Label>
      <p className="text-[10px] text-slate-500">Montants : remplissez toutes les mensualités ou aucune ; l'acompte prend la différence avec le total TTC.</p>
      <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <span className="text-[10px] text-slate-500">Acompte</span>
          <DateFrInput value={custom.acompte_date} onChange={(v) => setCustom({ ...custom, acompte_date: v })} testId="custom-date-acompte" />
        </div>
        {Array.from({ length: nb }, (_, i) => (
          <div key={i}>
            <span className="text-[10px] text-slate-500">Mensualité {i + 1}/{nb}</span>
            <div className="flex gap-1">
              <div className="flex-1"><DateFrInput value={(custom.dates_iso || [])[i] || ''} onChange={(v) => setListe('dates_iso', i, v)} testId={`custom-date-echeance-${i + 1}`} /></div>
              <Input value={(custom.montants_eur || [])[i] ?? ''} onChange={(e) => setListe('montants_eur', i, e.target.value.replace(/[^\d.,]/g, ''))}
                placeholder="€" inputMode="decimal" className="h-8 w-24 text-sm font-mono text-right" data-testid={`custom-montant-echeance-${i + 1}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
